// Reading a recipe in the admin's own browser, for importing on behalf of a household (#1562). A page that lets other
// sites read it (Mealime, BBC Good Food) is read here and sent with its link, as the apps send what the phone read;
// most do not (their CORS headers), and those the server tries next. Pure functions but the fetch, so
// tests/admin/recipeReader.test.js can check them without a browser.

const SCRIPT_LD_JSON = /<script[^>]*type\s*=\s*["']?application\/ld\+json["']?[^>]*>([\s\S]*?)<\/script>/gi;

function hasType(node, type) {
  const declared = node && node["@type"];
  const types = Array.isArray(declared) ? declared : [declared];
  return types.some((t) => typeof t === "string" && t.toLowerCase() === type.toLowerCase());
}

function findRecipeNode(node, depth = 0) {
  if (!node || depth > 6) return null;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findRecipeNode(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof node !== "object") return null;
  if (hasType(node, "Recipe")) return node;
  for (const key of ["@graph", "mainEntity", "itemListElement", "hasPart"]) {
    const found = findRecipeNode(node[key], depth + 1);
    if (found) return found;
  }
  return null;
}

function stringOf(value) {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(stringOf).find((s) => s) || "";
  if (value && typeof value === "object") return stringOf(value["@value"] || value.text || value.name || value.url || "");
  return "";
}

const ENTITIES = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: " ", frac12: "½", frac14: "¼", frac34: "¾", deg: "°" };

/** One line of recipe text: tags out, entities decoded, whitespace collapsed. */
export function cleanLine(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (match, code) => {
      if (code[0] === "#") {
        const n = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
        return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : match;
      }
      return ENTITIES[code.toLowerCase()] ?? match;
    })
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
}

function instructionLines(value) {
  const lines = [];
  const walk = (item, depth) => {
    if (!item || depth > 6) return;
    if (Array.isArray(item)) return item.forEach((i) => walk(i, depth + 1));
    if (typeof item === "string") return item.split(/\r?\n+/).forEach((line) => lines.push(line));
    if (typeof item !== "object") return;
    if (hasType(item, "HowToSection") || Array.isArray(item.itemListElement)) return walk(item.itemListElement, depth + 1);
    const text = stringOf(item.text) || stringOf(item.name);
    if (text) lines.push(text);
  };
  walk(value, 0);
  return lines.map(cleanLine).filter(Boolean);
}

function imageOf(value) {
  const list = Array.isArray(value) ? value : [value];
  for (const item of list) {
    const url = typeof item === "string" ? item : stringOf(item && (item.url || item.contentUrl));
    if (/^https?:\/\//i.test(url)) return url;
  }
  return null;
}

/** Minutes in an ISO 8601 duration ("PT1H15M"), or null. */
export function minutesOf(duration) {
  const match = typeof duration === "string" && duration.match(/^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/i);
  if (!match || match[0] === "P" || match[0] === "PT") return null;
  return (Number(match[1] || 0) * 1440) + (Number(match[2] || 0) * 60) + Number(match[3] || 0);
}

/** The first number in a yield ("4 servings", ["4", "4 servings"]), or null. */
export function servingsOf(recipeYield) {
  const match = stringOf(recipeYield).match(/\d+/);
  return match ? Number(match[0]) : null;
}

/** The recipe in a page's JSON-LD as fields, or null when the page publishes none. */
export function extractRecipeFields(html) {
  for (const [, raw] of String(html || "").matchAll(SCRIPT_LD_JSON)) {
    let data = null;
    for (const candidate of [raw.trim(), raw.replace(/<!--[\s\S]*?-->/g, "").trim()]) {
      try {
        data = JSON.parse(candidate);
        break;
      } catch {
        data = null;
      }
    }
    const node = findRecipeNode(data);
    if (!node) continue;
    const ingredients = (Array.isArray(node.recipeIngredient) ? node.recipeIngredient : [node.recipeIngredient])
      .map((line) => cleanLine(stringOf(line))).filter(Boolean);
    const instructions = instructionLines(node.recipeInstructions);
    if (!ingredients.length && !instructions.length) continue;
    return {
      title: cleanLine(stringOf(node.name)),
      servings: servingsOf(node.recipeYield),
      prepMinutes: minutesOf(stringOf(node.prepTime)),
      cookMinutes: minutesOf(stringOf(node.cookTime)),
      ingredients,
      instructions,
      imageUrl: imageOf(node.image)
    };
  }
  return null;
}

/**
 * The recipe on the page at `url`, read by this browser, or null when the site does not let another site read it,
 * or publishes no recipe. Never throws.
 */
export async function readRecipeInBrowser(url, fetchImpl = globalThis.fetch) {
  try {
    const res = await fetchImpl(url, { credentials: "omit" });
    if (!res.ok) return null;
    return extractRecipeFields(await res.text());
  } catch {
    return null;
  }
}

/** The recipe as the text the pipeline reads, in the shape the server writes for a page it read. */
export function recipeTextFromFields(fields) {
  const parts = [];
  if (fields.title) parts.push(`Title: ${fields.title}`);
  if (fields.servings) parts.push(`Servings: ${fields.servings}`);
  if (fields.ingredients.length) parts.push("Ingredients:\n" + fields.ingredients.map((line) => `- ${line}`).join("\n"));
  if (fields.instructions.length) parts.push("Instructions:\n" + fields.instructions.map((line, i) => `${i + 1}. ${line}`).join("\n"));
  return parts.join("\n\n");
}

/** Every http(s) link in a pasted list, in order, once each. */
export function linksIn(text) {
  const found = String(text || "").match(/https?:\/\/[^\s<>"']+/gi) || [];
  return [...new Set(found.map((link) => link.replace(/[),.;!?]+$/, "")))];
}

const INGREDIENTS_HEADING = /^(ingredients?)\b[:\s]*$/i;
const STEPS_HEADING = /^(instructions?|directions?|method|steps?|preparation)\b[:\s]*$/i;
const BULLET = /^\s*(?:[-*•·]|\d+[.)])\s+/;

/**
 * A draft from pasted recipe text: a first line that is not a heading becomes the title, lines under an
 * "Ingredients" heading the ingredients, lines under "Instructions" / "Directions" / "Method" the steps. Without the
 * headings, every line is a step: the admin moves the ingredients in the draft.
 */
export function splitRecipeText(text) {
  const lines = String(text || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const draft = { title: "", ingredients: [], instructions: [] };
  let section = null;
  lines.forEach((line, index) => {
    if (INGREDIENTS_HEADING.test(line)) { section = "ingredients"; return; }
    if (STEPS_HEADING.test(line)) { section = "instructions"; return; }
    if (index === 0 && !section) { draft.title = line.replace(/^title:\s*/i, ""); return; }
    const cleaned = line.replace(BULLET, "");
    if (section === "ingredients") draft.ingredients.push(cleaned);
    else draft.instructions.push(cleaned);
  });
  return draft;
}
